// src/pages/dashboard/accounts/modals/YouTubeModals.js

import React from 'react';
import { Modal, Button } from 'react-bootstrap';

/**
 * YouTubeModals Component
 *
 * Holds all the modals for YouTube:
 *   1) Connect YouTube modal
 *   2) Delete (Edit) YouTube modal
 *   3) Confirm Deletion
 *
 * Props (passed from Accounts.js):
 * ----------------------------------------------------------------
 * showYouTubeModal               : bool
 * setShowYouTubeModal            : function
 * youtubeAccounts                : array   // pending YouTube channels
 * selectedYouTubeIds             : array   // which channels are selected
 * handleYouTubeCheckboxChange    : function
 * handleConnectSelectedYouTube   : function
 *
 * showYouTubeDeleteModal         : bool
 * setShowYouTubeDeleteModal      : function
 * showYouTubeDeleteConfirmModal  : bool
 * setShowYouTubeDeleteConfirmModal : function
 * handleDeleteYouTubeAccounts    : function
 * handleDeleteYouTubeConfirm     : function
 *
 * accounts                       : array   // from Accounts.js
 */

export default function YouTubeModals({
  // CONNECT MODAL
  showYouTubeModal,
  setShowYouTubeModal,
  youtubeAccounts,
  selectedYouTubeIds,
  handleYouTubeCheckboxChange,
  handleConnectSelectedYouTube,

  // DELETE MODAL
  showYouTubeDeleteModal,
  setShowYouTubeDeleteModal,
  showYouTubeDeleteConfirmModal,
  setShowYouTubeDeleteConfirmModal,
  handleDeleteYouTubeAccounts,
  handleDeleteYouTubeConfirm,

  // All connected accounts from Accounts.js
  accounts
}) {
  // We pull out the 'YouTube' account object so we can list any connected channels
  const ytAcc = accounts
    ? accounts.find((acc) => acc.platform.toLowerCase() === 'youtube')
    : null;

  return (
    <>
      {/* ===================== CONNECT YOUTUBE MODAL ===================== */}
      <Modal show={showYouTubeModal} onHide={() => setShowYouTubeModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Select YouTube Channels</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {/* If youtubeAccounts is empty, we can display a quick message */}
          {(!youtubeAccounts || youtubeAccounts.length === 0) ? (
            <p>No YouTube channels found.</p>
          ) : (
            <ul className="list-group">
              {youtubeAccounts.map((acc) => {
                // Always use acc.youtubeId
                const ytId = acc.youtubeId;
                // Check whether it's in the "selectedYouTubeIds" array
                const checked = selectedYouTubeIds.includes(ytId);

                return (
                  <li key={ytId} className="list-group-item d-flex align-items-center">
                    <input
                      type="checkbox"
                      className="form-check-input me-2"
                      checked={checked}
                      onChange={() => handleYouTubeCheckboxChange(ytId)}
                    />
                    {acc.channelThumbnail && (
                      <img
                        src={acc.channelThumbnail}
                        alt={acc.channelName}
                        style={{
                          width: '30px',
                          height: '30px',
                          borderRadius: '50%',
                          marginRight: '8px'
                        }}
                      />
                    )}
                    <span>{acc.channelName}</span>
                  </li>
                );
              })}
            </ul>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowYouTubeModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleConnectSelectedYouTube}>
            Connect
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ===================== DELETE/EDIT YOUTUBE MODAL ===================== */}
      <Modal show={showYouTubeDeleteModal} onHide={() => setShowYouTubeDeleteModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Delete YouTube Channels</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {/* If no connected accounts, show a quick message */}
          {(!ytAcc || !ytAcc.connectedPages || ytAcc.connectedPages.length === 0) ? (
            <p>No connected YouTube channels to delete.</p>
          ) : (
            <ul className="list-group">
              {ytAcc.connectedPages.map((item) => {
                const ytId = item.youtubeId;
                const checked = selectedYouTubeIds.includes(ytId);

                return (
                  <li key={ytId} className="list-group-item d-flex align-items-center">
                    <input
                      type="checkbox"
                      className="form-check-input me-2"
                      checked={checked}
                      onChange={() => handleYouTubeCheckboxChange(ytId)}
                    />
                    {item.channelThumbnail && (
                      <img
                        src={item.channelThumbnail}
                        alt={item.channelName}
                        style={{
                          width: '30px',
                          height: '30px',
                          borderRadius: '50%',
                          marginRight: '8px'
                        }}
                      />
                    )}
                    <span>{item.channelName}</span>
                  </li>
                );
              })}
            </ul>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowYouTubeDeleteModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDeleteYouTubeAccounts}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ===================== CONFIRM DELETE YOUTUBE MODAL ===================== */}
      <Modal
        show={showYouTubeDeleteConfirmModal}
        onHide={() => setShowYouTubeDeleteConfirmModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Confirm Deletion</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Are you sure you want to delete these YouTube channels?</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowYouTubeDeleteConfirmModal(false)}>
            No
          </Button>
          <Button variant="danger" onClick={handleDeleteYouTubeConfirm}>
            Yes, Delete
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

